/**
 * The board's deadline cell: the date, plus a readable relative statement („heute 14:00",
 * „in 3 Tagen, 12:00", „vor 2 Tagen"). Replaces „in 0 T" / „seit 41 T", which read as code and
 * miscounted (Testbericht 15.10.2026: „in 0 T" for a deadline already past, „in 1 Tagen" for today).
 *
 * Calendar days are counted in German local time (Europe/Berlin), not in UTC and not in the
 * browser's zone: a tender is due on the day its German buyer means.
 *
 * A date without a time is stored by the server as **UTC midnight** (tri-server
 * docs/tender-dates.md), which in Berlin reads as 01:00 or 02:00 — the invented „01:00" of the
 * Testbericht. So UTC midnight (and Berlin midnight) count as "date only": shown without a time,
 * on the UTC calendar date, and due until the end of that day.
 */

const ZONE = "Europe/Berlin";

/** Year, month, day, hour and minute of an instant in German local time. */
const berlinParts = (date: Date) => {
  const parts = new Intl.DateTimeFormat("de-DE", {
    timeZone: ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type: string) => Number(parts.find(part => part.type === type)?.value ?? 0);
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour"), minute: get("minute") };
};

/** Whole calendar days from `now` to `deadline`, both read as Berlin dates. */
export const berlinCalendarDaysBetween = (now: Date, deadline: Date): number => {
  const a = berlinParts(now);
  const b = berlinParts(deadline);
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000
  );
};

export type DeadlineView = {
  /** „22.10.2026" */
  date: string;
  /** Relative statement, already worded; see `describeDeadline`. */
  relative: string;
  /** Within the next seven days and not yet past — the rows the board highlights. */
  highlight: boolean;
  past: boolean;
};

/** The words, injected so the helper stays testable without i18n. */
export type DeadlineWords = {
  today: (time: string | null) => string;
  tomorrow: (time: string | null) => string;
  inDays: (days: number, time: string | null) => string;
  past: (days: number) => string;
  pastToday: (time: string | null) => string;
};

/** Days ahead that count as "this week" — the server's `criticalDays`. */
export const HIGHLIGHT_DAYS = 7;

export const describeDeadline = (
  iso: string | null,
  now: Date,
  words: DeadlineWords
): DeadlineView | null => {
  if (!iso) return null;
  const deadline = new Date(iso);
  if (Number.isNaN(deadline.getTime())) return null;

  const utcMidnight =
    deadline.getUTCHours() === 0 && deadline.getUTCMinutes() === 0 && deadline.getUTCSeconds() === 0;
  // A UTC-midnight date is read on its UTC calendar day; noon of that day is the same day in Berlin.
  const dayOf = utcMidnight ? new Date(deadline.getTime() + 12 * 3_600_000) : deadline;
  const local = berlinParts(dayOf);
  const dateOnly = utcMidnight || (local.hour === 0 && local.minute === 0);
  const time = dateOnly
    ? null
    : `${String(local.hour).padStart(2, "0")}:${String(local.minute).padStart(2, "0")}`;
  const date = `${String(local.day).padStart(2, "0")}.${String(local.month).padStart(2, "0")}.${local.year}`;

  const days = berlinCalendarDaysBetween(now, dayOf);
  // A date-only deadline runs to the end of its day; one with a time ends at that minute.
  const past = dateOnly ? days < 0 : deadline.getTime() < now.getTime();

  let relative: string;
  if (past) relative = days === 0 ? words.pastToday(time) : words.past(Math.abs(days));
  else if (days === 0) relative = words.today(time);
  else if (days === 1) relative = words.tomorrow(time);
  else relative = words.inDays(days, time);

  return { date, relative, highlight: !past && days <= HIGHLIGHT_DAYS, past };
};
